import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { DownloadLinks } from 'app/pages/RegistrationFormPage';
import axios, { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { Ticket } from 'lucide-react';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { EventFieldType } from 'types/EventField';
import { onResize } from 'utils/embed';
import { formatFieldsForAttendeesFormsRoute } from 'utils/registrationForms';
import {
  API_REQUEST_STATUS_FAIL,
  API_REQUEST_STATUS_NOT_SENT,
  API_REQUEST_STATUS_PENDING,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import { transformArrayToSelectOptions } from 'utils/transformArrayToSelectOptions';
import AddonsAndTopUpsSection from '../AddonsAndTopUpsSection/AddonsAndTopUpsSection';
import CircularProgressBar from '../Common/CircularProgressBar';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import ShareTicketDialog from '../ShareTicketDialog/ShareTicketDialog';
import RegistrationFormDownloadTicket from './RegistrationFormDownloadTicket';

interface RegistrationFormProps {
  isDisplayedIndividually: boolean;
  registrationForm: any;
  fields: EventFieldType[];
  orderId: string;
  eventId: string;
  displayedIndex?: number;
  handleTicketsButtonEnableStatus?: (boolean) => void;
  downloadLinks?: DownloadLinks | null;
  expanded?: boolean;
  registrationFormsCanBeEditedAfterSubmission: boolean;
  displayTickets?: boolean;
}

interface RequestPayload {
  order_id?: string;
  event_id?: string;
  fields: {
    [key: string]: Object;
  };
}

const RegistrationFormAccordion: React.FC<RegistrationFormProps> = ({
  registrationForm: {
    name,
    id_order_item,
    index,
    ticket_type_id,
    ticketTypeDescription,
    data: registrationFormData,
    addons,
    wallet_credit,
  },
  fields,
  orderId,
  eventId,
  displayedIndex,
  isDisplayedIndividually,
  handleTicketsButtonEnableStatus,
  downloadLinks = null,
  expanded = false,
  registrationFormsCanBeEditedAfterSubmission,
  displayTickets = true,
}) => {
  const { t } = useTranslation();
  const [requestStatus, setRequestStatus] = useState<string>(
    API_REQUEST_STATUS_NOT_SENT,
  );
  const [isExpanded, setIsExpanded] = useState(expanded);
  const { pathname } = useLocation();

  const ticketDoesntHaveDefaultRegistrationFormsValues =
    registrationFormData === null;

  const getRegistrationFormsDefaultValues = () => {
    if (!registrationFormData) return {};

    const result = {};

    for (let key in registrationFormData) {
      const newKey = `${ticket_type_id}-${key}-${index}`;
      if (
        registrationFormData[key] &&
        Array.isArray(registrationFormData[key])
      ) {
        const fieldData = (
          fields.find(field => field.id === key) as EventFieldType
        ).meta.options;

        // registrationFormData[key] contains the indexes of the selected options
        const parsedValues = registrationFormData[key].map(
          optionIndex => fieldData[optionIndex],
        );

        result[newKey] = transformArrayToSelectOptions(parsedValues);
      } else {
        result[newKey] = registrationFormData[key];
      }
    }

    return result;
  };

  const methods = useForm({
    defaultValues: {
      ...getRegistrationFormsDefaultValues(),
    },
  });
  const {
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;
  const formValues = watch();

  const submitForm = async data => {
    const requestPayload: RequestPayload = {
      fields: {
        [id_order_item]: {
          [index]: formatFieldsForAttendeesFormsRoute(data),
        },
      },
    };

    if (orderId) {
      requestPayload.order_id = orderId;
    } else {
      requestPayload.event_id = eventId;
    }

    setRequestStatus(API_REQUEST_STATUS_PENDING);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/order/attendees_form`,
        requestPayload,
      );

      if (handleTicketsButtonEnableStatus) {
        handleTicketsButtonEnableStatus(
          response.data.mustCompleteRegistrationForms,
        );
      }

      setRequestStatus(API_REQUEST_STATUS_SUCCESS);
    } catch (error) {
      if (error instanceof AxiosError) {
        setRequestStatus(API_REQUEST_STATUS_FAIL);
      }
    }
  };

  const ticketHasErrors = (ticketIndex: number) => {
    const indexOfError = Object.keys(errors).findIndex(
      error => error.split('-')[2] === ticketIndex.toString(),
    );
    return indexOfError > -1 ? true : false;
  };

  const getCompletionPercentage = (ticketId, ticketIndex) => {
    if (
      !ticketDoesntHaveDefaultRegistrationFormsValues ||
      requestStatus === API_REQUEST_STATUS_SUCCESS
    )
      return 100;
    if (isEmpty(formValues)) return 0;

    let numberOfCompletedFields = Object.keys(formValues).filter(
      input =>
        input.split('-')[0] === ticketId.toString() &&
        input.split('-')[2] === ticketIndex.toString() &&
        (!isEmpty(formValues[input]) || formValues[input] === true),
      // errors[`${`${ticketId}-${field.id}-${ticketIndex}`}`]
    ).length;

    // the following lines of code cancel the 'completed' flag
    // of an email input if it these conditons are true all together:
    // - it is mandatory
    // - it has an error associated
    // - its value is not null (this condition exists because if the value is null,
    // the field is not added to numberOfCompletedFields. if you remove it, this is going
    // to lead to a negative completation value )
    const emailFieldIndex = fields.findIndex(field => field.type === 'email');

    if (emailFieldIndex >= 0) {
      const fieldKey = `${ticketId}-${fields[emailFieldIndex].id}-${ticketIndex}`;
      if (
        fields[emailFieldIndex].mandatory &&
        errors[fieldKey] &&
        formValues[fieldKey]
      ) {
        numberOfCompletedFields--;
      }
    }

    const percentage = (numberOfCompletedFields * 100) / fields.length;

    return Math.round(percentage);
  };

  const handleExpandAccordion = () => setIsExpanded(!isExpanded);

  const saveButtonIsDisabled = registrationFormsCanBeEditedAfterSubmission
    ? !registrationFormsCanBeEditedAfterSubmission
    : !ticketDoesntHaveDefaultRegistrationFormsValues ||
      requestStatus !== API_REQUEST_STATUS_NOT_SENT;

  return (
    <div
      className={
        isDisplayedIndividually ? '' : 'not-first:border-t :border-neutral-40'
      }
    >
      <Accordion
        expanded={isDisplayedIndividually ? true : isExpanded}
        defaultExpanded={!!isDisplayedIndividually}
        className={`registration-forms-accordion !bg-transparent${
          !!isDisplayedIndividually ? ' is-displayed-individually' : ''
        }`}
        TransitionProps={{
          unmountOnExit: true,
          mountOnEnter: true,
          onExited: onResize,
          onEntered: onResize,
        }}
        disableGutters
      >
        {isDisplayedIndividually ? (
          <div className="flex items-center gap-[8px]">
            <Ticket size={20} />
            <p className="p1-medium"> {name} </p>
          </div>
        ) : (
          <AccordionSummary
            expandIcon={
              <button className="bg-neutral-40 rounded-full">
                <ExpandMoreIcon
                  fontSize="medium"
                  onClick={handleExpandAccordion}
                />
              </button>
            }
          >
            <div className="flex w-full justify-between items-center select-none cursor-default border-none pr-[8px]">
              <div className="flex">
                <CircularProgressBar
                  percent={getCompletionPercentage(ticket_type_id, index)}
                />
                {displayedIndex !== undefined ? (
                  <p className="p1-medium ml-[8px] mr-[12px]">
                    #{displayedIndex + 1}
                  </p>
                ) : null}
                <div>
                  <h4 className="title-xs"> {name} </h4>
                  {ticketHasErrors(index) && (
                    <p className="standard-input-error">
                      {t('inputErrors.formHasErrors')}
                    </p>
                  )}
                </div>
              </div>
              {isDisplayedIndividually ? null : (
                <ShareTicketDialog
                  idOrderItem={id_order_item}
                  formIndex={index}
                  eventId={eventId}
                  buttonText={t('registrationFormsPage.shareTicket')}
                />
              )}
            </div>
          </AccordionSummary>
        )}
        <AccordionDetails>
          <div className={`${isDisplayedIndividually ? '' : 'md:px-[16px]'}`}>
            {ticketTypeDescription ? (
              <div className="pl-[24px] mb-[16px] whitespace-pre-line">
                {ticketTypeDescription}
              </div>
            ) : null}
            <div className="my-[12px]">
              <AddonsAndTopUpsSection
                addons={addons}
                wallet_credit={wallet_credit}
              />
            </div>
            {pathname === '/order/complete-attendee-form' && displayTickets ? (
              <RegistrationFormDownloadTicket
                ticketDoesntHaveDefaultRegistrationFormsValues={
                  ticketDoesntHaveDefaultRegistrationFormsValues
                }
                requestStatus={requestStatus}
                downloadLinks={downloadLinks}
              />
            ) : null}
            {isDisplayedIndividually ? (
              <>
                <hr className="my-4" />
                <h2 className="title-md my-[16px]">
                  {t('registrationFormPage.aboutYou')}
                </h2>
              </>
            ) : null}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(submitForm)}>
                <RegistrationForm
                  ticketId={ticket_type_id}
                  ticketIndex={index}
                  fields={fields}
                  requestStatus={requestStatus}
                  disabled={saveButtonIsDisabled}
                />
              </form>
            </FormProvider>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default RegistrationFormAccordion;
